"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get AiModel () {
        return AiModel;
    },
    get AiSettingsSchema () {
        return AiSettingsSchema;
    },
    get AiSettingsSchemaWithDefault () {
        return AiSettingsSchemaWithDefault;
    },
    get GoalFramework () {
        return GoalFramework;
    },
    get reviewCycleInsightsSettings () {
        return reviewCycleInsightsSettings;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const _employeesummary = require("./employee-summary");
const AiModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    embedding: _zod.z.string(),
    created_at: _zod.z.date(),
    updated_at: _zod.z.date()
});
const AiAuthoringAssistantPreferredWritingStyle = _zod.z.enum([
    "casual",
    "businessProfessional",
    "businessCasual",
    "formal"
]);
const authoringAssistantEnabled = _zod.z.boolean();
const formQuestionAssistantEnabled = _zod.z.boolean();
const authoringAssistantSettings = _zod.z.object({
    preferredWritingStyle: AiAuthoringAssistantPreferredWritingStyle,
    disallowedWords: _zod.z.array(_zod.z.string())
});
const reviewSummarizationEnabled = _zod.z.boolean();
const reviewSummarizationSettings = _zod.z.object({
    strengths: _zod.z.boolean(),
    quotes: _zod.z.boolean(),
    actions: _zod.z.boolean(),
    tone: _zod.z.boolean(),
    opinions: _zod.z.boolean(),
    summary: _zod.z.boolean()
});
const reviewCycleInsightsEnabled = _zod.z.boolean();
const reviewCycleInsightsSettings = _zod.z.object({
    summaryCompleteNotification: _zod.z.boolean(),
    reviewToneDistribution: _zod.z.boolean(),
    highlightedReviews: _zod.z.boolean(),
    keyStrengthsAndOpportunities: _zod.z.boolean(),
    commonThemesPositiveReviews: _zod.z.boolean(),
    commonThemesNegativeReviews: _zod.z.boolean(),
    recurringActionItems: _zod.z.boolean(),
    disconnectsAnalysis: _zod.z.boolean()
});
const engagementAnalysisEnabled = _zod.z.boolean();
const engagementCohortInsightsEnabled = _zod.z.boolean();
const meetingWorkspaceInsightsEnabled = _zod.z.boolean();
const meetingWorkspaceInsightsSettings = _zod.z.object({
    progressAndGrowth: _zod.z.boolean(),
    keyAccomplishmentsAndWins: _zod.z.boolean(),
    developmentAreasAndGrowthOpportunities: _zod.z.boolean(),
    notableDiscussionsAndDecisions: _zod.z.boolean(),
    actionItemsAnalysis: _zod.z.boolean()
});
const teamMeetingWorkspaceInsightsEnabled = _zod.z.boolean();
/**
 * Naming note: the settings key is `actionItemsAnalysis` (plural) to match the
 * existing 1:1 settings convention, while the corresponding JSON output field
 * in the prompt response is `actionItemAnalysis` (singular). Each side reads
 * its own name — do not unify without coordinating prompt template + schema.
 */ const teamMeetingWorkspaceInsightsSettings = _zod.z.object({
    recurringThemesAndTopics: _zod.z.boolean(),
    keyAccomplishmentsAndWins: _zod.z.boolean(),
    recurringChallengesAndBlockers: _zod.z.boolean(),
    notableDiscussionsAndDecisions: _zod.z.boolean(),
    actionItemsAnalysis: _zod.z.boolean()
});
const preMeetingPrepEnabled = _zod.z.boolean();
const preMeetingPrepSettings = _zod.z.object({
    suggestedAgendaItems: _zod.z.boolean(),
    goalsForDiscussion: _zod.z.boolean(),
    recentFeedback: _zod.z.boolean(),
    recentReviews: _zod.z.boolean(),
    surveyInsights: _zod.z.boolean()
});
const postMeetingAnalysisEnabled = _zod.z.boolean();
const postMeetingAnalysisSettings = _zod.z.object({
    decisionsMade: _zod.z.boolean(),
    missedCommitments: _zod.z.boolean(),
    participationAndInclusion: _zod.z.boolean(),
    qualityOfDiscussion: _zod.z.boolean(),
    timeManagement: _zod.z.boolean(),
    meetingCoaching: _zod.z.boolean()
});
const GoalFramework = _zod.z.enum([
    "generalGuidance",
    "smart",
    "custom"
]);
const goalCoachingEnabled = _zod.z.boolean();
const goalCoachingSettings = _zod.z.object({
    goalFramework: GoalFramework,
    goalSettingGuidelines: _zod.z.string()
});
const employeeInsightsEnabled = _zod.z.boolean();
const employeeCoachingEnabled = _zod.z.boolean();
const formAuthoringCoachEnabled = _zod.z.boolean();
const formAuthoringCoachSettings = _zod.z.object({
    vagueResponses: _zod.z.boolean(),
    speculativeFeedback: _zod.z.boolean(),
    haloAndHornsBiases: _zod.z.boolean(),
    conflictingRatings: _zod.z.boolean(),
    positiveNegativeImbalance: _zod.z.boolean(),
    additionalGuidelines: _zod.z.string()
});
const formBuildingCoachEnabled = _zod.z.boolean();
const surveyInsightsEnabled = _zod.z.boolean();
const surveyInsightsSettings = _zod.z.object({
    sentimentAnalysis: _zod.z.boolean(),
    keyThemesAndPatterns: _zod.z.boolean(),
    outliersAndNotableResponses: _zod.z.boolean(),
    actionableInsightsAndRecommendations: _zod.z.boolean(),
    demographicInsights: _zod.z.boolean(),
    crossQuestionInsights: _zod.z.boolean().default(true)
});
const conversationalAiEnabled = _zod.z.boolean();
const conversationalAiSettings = _zod.z.object({
    askHrEnabled: _zod.z.boolean().default(true),
    goalCreationEnabled: _zod.z.boolean().default(true),
    formCreationEnabled: _zod.z.boolean().default(true),
    interviewerEnabled: _zod.z.boolean().default(false)
});
const AiSettingsSchema = _zod.z.object({
    /* AI authoring assistant */ authoringAssistantEnabled,
    formQuestionAssistantEnabled,
    authoringAssistantSettings,
    /* Review summarization assistant */ reviewSummarizationEnabled,
    reviewSummarizationSettings,
    /* Review cycle insights assistant */ reviewCycleInsightsEnabled,
    reviewCycleInsightsSettings,
    /* Engagement analysis assistant */ engagementAnalysisEnabled,
    engagementCohortInsightsEnabled,
    /* Meeting workspace insights assistant */ meetingWorkspaceInsightsEnabled,
    meetingWorkspaceInsightsSettings,
    /* Team meeting workspace insights assistant */ teamMeetingWorkspaceInsightsEnabled,
    teamMeetingWorkspaceInsightsSettings,
    /* Pre-Meeting Preparation assistant */ preMeetingPrepEnabled,
    preMeetingPrepSettings,
    /* Post Meeting Analysis */ postMeetingAnalysisEnabled,
    postMeetingAnalysisSettings,
    /* Goal Coaching */ goalCoachingEnabled,
    goalCoachingSettings,
    /* Employee Insights & Coaching */ employeeInsightsEnabled,
    employeeCoachingEnabled,
    /* Form Authoring Coaching */ formAuthoringCoachEnabled,
    formAuthoringCoachSettings,
    /* Form Building Coaching */ formBuildingCoachEnabled,
    /* Survey insights assistant */ surveyInsightsEnabled,
    surveyInsightsSettings,
    /* Conversational AI assistant */ conversationalAiSettings
});
const AiSettingsSchemaWithDefault = _zod.z.object({
    /* AI authoring assistant */ authoringAssistantEnabled: authoringAssistantEnabled.default(true),
    formQuestionAssistantEnabled: formQuestionAssistantEnabled.default(true),
    authoringAssistantSettings: authoringAssistantSettings.default({
        preferredWritingStyle: "businessProfessional",
        disallowedWords: []
    }),
    /* Review summarization assistant */ reviewSummarizationEnabled: reviewSummarizationEnabled.default(true),
    reviewSummarizationSettings: reviewSummarizationSettings.default({
        strengths: true,
        quotes: true,
        actions: true,
        tone: true,
        opinions: true,
        summary: true
    }),
    /* Review cycle insights assistant */ reviewCycleInsightsEnabled: reviewCycleInsightsEnabled.default(true),
    reviewCycleInsightsSettings: reviewCycleInsightsSettings.default({
        summaryCompleteNotification: false,
        reviewToneDistribution: true,
        highlightedReviews: true,
        keyStrengthsAndOpportunities: true,
        commonThemesPositiveReviews: true,
        commonThemesNegativeReviews: true,
        recurringActionItems: true,
        disconnectsAnalysis: true
    }),
    /* Engagement analysis assistant */ engagementAnalysisEnabled: engagementAnalysisEnabled.default(true),
    engagementCohortInsightsEnabled: engagementCohortInsightsEnabled.default(true),
    /* Meeting workspace insights assistant */ meetingWorkspaceInsightsEnabled: meetingWorkspaceInsightsEnabled.default(true),
    meetingWorkspaceInsightsSettings: meetingWorkspaceInsightsSettings.default({
        progressAndGrowth: true,
        keyAccomplishmentsAndWins: true,
        developmentAreasAndGrowthOpportunities: true,
        notableDiscussionsAndDecisions: true,
        actionItemsAnalysis: true
    }),
    /* Team meeting workspace insights assistant */ teamMeetingWorkspaceInsightsEnabled: teamMeetingWorkspaceInsightsEnabled.default(true),
    teamMeetingWorkspaceInsightsSettings: teamMeetingWorkspaceInsightsSettings.default({
        recurringThemesAndTopics: true,
        keyAccomplishmentsAndWins: true,
        recurringChallengesAndBlockers: true,
        notableDiscussionsAndDecisions: true,
        actionItemsAnalysis: true
    }),
    /* Pre-Meeting Preparation assistant */ preMeetingPrepEnabled: preMeetingPrepEnabled.default(true),
    preMeetingPrepSettings: preMeetingPrepSettings.default({
        suggestedAgendaItems: true,
        goalsForDiscussion: true,
        recentFeedback: true,
        recentReviews: true,
        surveyInsights: true
    }),
    /* Post Meeting Analysis */ postMeetingAnalysisEnabled: postMeetingAnalysisEnabled.default(true),
    postMeetingAnalysisSettings: postMeetingAnalysisSettings.default({
        decisionsMade: true,
        missedCommitments: true,
        participationAndInclusion: true,
        qualityOfDiscussion: true,
        timeManagement: true,
        meetingCoaching: true
    }),
    /* Goal Coaching */ goalCoachingEnabled: goalCoachingEnabled.default(true),
    goalCoachingSettings: goalCoachingSettings.default({
        goalFramework: "generalGuidance",
        goalSettingGuidelines: ""
    }),
    /* Employee Insights & Coaching */ employeeInsightsEnabled: employeeInsightsEnabled.default(true),
    employeeCoachingEnabled: employeeCoachingEnabled.default(true),
    // Internal-only sampler override (PY-13140). Deliberately absent from
    // AiSettingsSchema and the settings PATCH contract: settable only by a
    // direct DB write, so no customer-facing strategy switch exists.
    employeeInsightsSamplingStrategy: _employeesummary.SamplingStrategySchema.optional(),
    /* Form Authoring Coaching */ formAuthoringCoachEnabled: formAuthoringCoachEnabled.default(true),
    formAuthoringCoachSettings: formAuthoringCoachSettings.default({
        vagueResponses: true,
        speculativeFeedback: true,
        haloAndHornsBiases: true,
        conflictingRatings: true,
        positiveNegativeImbalance: true,
        additionalGuidelines: ""
    }),
    /* Form Building Coaching */ formBuildingCoachEnabled: formBuildingCoachEnabled.default(true),
    /* Survey insights assistant */ surveyInsightsEnabled: surveyInsightsEnabled.default(true),
    surveyInsightsSettings: surveyInsightsSettings.default({
        sentimentAnalysis: true,
        keyThemesAndPatterns: true,
        outliersAndNotableResponses: true,
        actionableInsightsAndRecommendations: true,
        demographicInsights: true,
        crossQuestionInsights: true
    }),
    /* Conversational AI assistant */ conversationalAiEnabled: conversationalAiEnabled.optional(),
    conversationalAiSettings: conversationalAiSettings.optional()
}).transform((data)=>{
    const { conversationalAiEnabled: legacyEnabled, ...rest } = data;
    // Determine final conversationalAiSettings value
    let conversationalAiSettings = data.conversationalAiSettings;
    // Migration: If legacy field exists but new settings don't, migrate the value
    if (legacyEnabled !== undefined && conversationalAiSettings === undefined) {
        conversationalAiSettings = {
            askHrEnabled: legacyEnabled,
            goalCreationEnabled: true,
            formCreationEnabled: true,
            interviewerEnabled: false
        };
    }
    // Apply defaults if still undefined
    if (conversationalAiSettings === undefined) {
        conversationalAiSettings = {
            askHrEnabled: true,
            goalCreationEnabled: true,
            formCreationEnabled: true,
            interviewerEnabled: false
        };
    }
    return {
        ...rest,
        conversationalAiSettings
    };
}).default({
    authoringAssistantEnabled: true,
    formQuestionAssistantEnabled: true,
    authoringAssistantSettings: {
        preferredWritingStyle: "businessCasual",
        disallowedWords: []
    },
    reviewSummarizationEnabled: true,
    reviewSummarizationSettings: {
        strengths: true,
        quotes: true,
        actions: true,
        tone: true,
        opinions: true,
        summary: true
    },
    reviewCycleInsightsEnabled: true,
    reviewCycleInsightsSettings: {
        summaryCompleteNotification: false,
        reviewToneDistribution: true,
        highlightedReviews: true,
        keyStrengthsAndOpportunities: true,
        commonThemesPositiveReviews: true,
        commonThemesNegativeReviews: true,
        recurringActionItems: true,
        disconnectsAnalysis: true
    },
    engagementAnalysisEnabled: true,
    engagementCohortInsightsEnabled: true,
    meetingWorkspaceInsightsEnabled: true,
    meetingWorkspaceInsightsSettings: {
        progressAndGrowth: true,
        keyAccomplishmentsAndWins: true,
        developmentAreasAndGrowthOpportunities: true,
        notableDiscussionsAndDecisions: true,
        actionItemsAnalysis: true
    },
    teamMeetingWorkspaceInsightsEnabled: true,
    teamMeetingWorkspaceInsightsSettings: {
        recurringThemesAndTopics: true,
        keyAccomplishmentsAndWins: true,
        recurringChallengesAndBlockers: true,
        notableDiscussionsAndDecisions: true,
        actionItemsAnalysis: true
    },
    preMeetingPrepEnabled: true,
    preMeetingPrepSettings: {
        suggestedAgendaItems: true,
        goalsForDiscussion: true,
        recentFeedback: true,
        recentReviews: true,
        surveyInsights: true
    },
    postMeetingAnalysisEnabled: true,
    postMeetingAnalysisSettings: {
        decisionsMade: true,
        missedCommitments: true,
        participationAndInclusion: true,
        qualityOfDiscussion: true,
        timeManagement: true,
        meetingCoaching: true
    },
    goalCoachingEnabled: true,
    goalCoachingSettings: {
        goalFramework: "generalGuidance",
        goalSettingGuidelines: ""
    },
    employeeInsightsEnabled: true,
    employeeCoachingEnabled: true,
    formAuthoringCoachEnabled: true,
    formAuthoringCoachSettings: {
        vagueResponses: true,
        speculativeFeedback: true,
        haloAndHornsBiases: true,
        conflictingRatings: true,
        positiveNegativeImbalance: true,
        additionalGuidelines: ""
    },
    formBuildingCoachEnabled: true,
    surveyInsightsEnabled: true,
    surveyInsightsSettings: {
        sentimentAnalysis: true,
        keyThemesAndPatterns: true,
        outliersAndNotableResponses: true,
        actionableInsightsAndRecommendations: true,
        demographicInsights: true,
        crossQuestionInsights: true
    },
    conversationalAiSettings: {
        askHrEnabled: true,
        goalCreationEnabled: true,
        formCreationEnabled: true,
        interviewerEnabled: false
    }
});
